import { useState, useEffect, useMemo, useRef } from 'react';
import { ContractContent, ContractField, FieldVisibility } from '../../types/contracts';
import { useStore } from '../../store/useStore';
import { Eye, EyeOff, Lock, Users, Plus, Trash2, User, ChevronDown, ChevronUp, PenTool, Code } from 'lucide-react';

interface ContractEditorProps {
  initialContent?: ContractContent;
  initialLeadId?: string | null;
  initialBuyerLeadId?: string | null;
  initialSellerLeadId?: string | null;
  onSave: (content: ContractContent, leadId: string | null, buyerLeadId: string | null, sellerLeadId: string | null) => void;
  isDraft: boolean;
}

const DEFAULT_TERMS = `This Purchase Agreement ("Agreement") is made on this day between {{buyer_name}} ("Buyer") and {{seller_name}} ("Seller") for the property located at {{property_address}}.

1. PURCHASE PRICE: Buyer agrees to pay Seller the sum of {{purchase_price}} for the Property.
2. EARNEST MONEY: Buyer shall deposit earnest money in the amount of {{earnest_money}} within 3 days of execution.
3. CLOSING DATE: The transaction shall close on or before {{closing_date}}.

Additional Terms:
{{additional_terms}}`;

const VISIBILITY_OPTIONS: { value: FieldVisibility; label: string; color: string; icon: any }[] = [
  { value: 'all', label: 'All Parties', color: 'text-[var(--t-success)] bg-[var(--t-success-dim)]', icon: Users },
  { value: 'buyer', label: 'Buyer Only', color: 'text-[var(--t-info)] bg-[var(--t-info-dim)]', icon: Eye },
  { value: 'seller', label: 'Seller Only', color: 'text-orange-400 bg-orange-400/10', icon: EyeOff },
  { value: 'agent_only', label: 'Agent Only', color: 'text-[var(--t-error)] bg-[var(--t-error)]/10', icon: Lock },
];

function VisibilityBadge({ visibility, onChange }: { visibility: FieldVisibility; onChange: (v: FieldVisibility) => void }) {
  const [open, setOpen] = useState(false);
  const current = VISIBILITY_OPTIONS.find(o => o.value === visibility) || VISIBILITY_OPTIONS[0];
  const Icon = current.icon;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${current.color}`}
        title={current.label}
      >
        <Icon size={10} />
        {current.label}
        <ChevronDown size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-30 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl shadow-xl overflow-hidden min-w-[140px]">
            {VISIBILITY_OPTIONS.map(opt => {
              const OptIcon = opt.icon;
              return (
                <button
                  key={opt.value}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors hover:bg-[var(--t-surface-hover)] ${opt.value === visibility ? opt.color : 'text-[var(--t-text-muted)]'}`}
                >
                  <OptIcon size={12} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Parses the text to find {{field_id}} tokens
function parseTerms(terms: string) {
  const parts: Array<{ type: 'text' | 'field'; content?: string; id?: string }> = [];
  const regex = /\{\{([^}]+)\}\}/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(terms)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: terms.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'field', id: match[1].trim() });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < terms.length) {
    parts.push({ type: 'text', content: terms.slice(lastIndex) });
  }
  return parts;
}

export function ContractEditor({ 
  initialContent, 
  initialLeadId, 
  initialBuyerLeadId, 
  initialSellerLeadId, 
  onSave, 
  isDraft 
}: ContractEditorProps) {
  const { leads } = useStore();
  const [selectedBuyerLeadId, setSelectedBuyerLeadId] = useState<string | null>(initialBuyerLeadId || null);
  const [selectedSellerLeadId, setSelectedSellerLeadId] = useState<string | null>(initialSellerLeadId || null);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const isTextArea = e.currentTarget.tagName.toLowerCase() === 'textarea';
    if (e.key === 'Tab' || (e.key === 'Enter' && !isTextArea)) {
      e.preventDefault();
      const container = previewContainerRef.current;
      if (!container) return;
      const inputs = Array.from(container.querySelectorAll('input, textarea')) as (HTMLInputElement | HTMLTextAreaElement)[];
      const currentIndex = inputs.indexOf(e.currentTarget);
      if (currentIndex !== -1) {
        const dir = e.shiftKey ? -1 : 1;
        let nextIndex = currentIndex + dir;
        if (nextIndex < 0) nextIndex = inputs.length - 1;
        if (nextIndex >= inputs.length) nextIndex = 0;
        inputs[nextIndex]?.focus();
        inputs[nextIndex]?.select();
      }
    }
  };

  const [fields, setFields] = useState<ContractField[]>(initialContent?.fields || [
    { id: 'buyer_name', name: 'Buyer Name', value: '', visibility: 'all', type: 'text' },
    { id: 'seller_name', name: 'Seller Name', value: '', visibility: 'all', type: 'text' },
    { id: 'property_address', name: 'Property Address', value: '', visibility: 'all', type: 'text' },
    { id: 'purchase_price', name: 'Purchase Price', value: '', visibility: 'all', type: 'currency' },
    { id: 'earnest_money', name: 'Earnest Money', value: '', visibility: 'all', type: 'currency' },
    { id: 'closing_date', name: 'Closing Date', value: '', visibility: 'all', type: 'date' },
    { id: 'commission', name: 'Agent Commission', value: '', visibility: 'agent_only', type: 'currency' },
    { id: 'additional_terms', name: 'Additional Terms', value: '', visibility: 'all', type: 'textarea' },
  ]);

  const [terms, setTerms] = useState(initialContent?.terms || DEFAULT_TERMS);
  
  // Synchronize fields list with terms tags in real-time
  useEffect(() => {
    const parts = parseTerms(terms);
    const tagIds = new Set(parts.filter(p => p.type === 'field').map(p => p.id));
    
    setFields(prev => {
      // Find missing fields
      const missingFields: ContractField[] = [];
      tagIds.forEach(id => {
        if (id && !prev.some(f => f.id === id)) {
          // Derive user-friendly name from the snake_case id
          const name = id
            .split('_')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
          
          let type: 'text' | 'date' | 'number' | 'currency' | 'textarea' = 'text';
          const nameLower = id.toLowerCase();
          if (nameLower.includes('date')) {
            type = 'date';
          } else if (nameLower.includes('price') || nameLower.includes('fee') || nameLower.includes('money') || nameLower.includes('deposit') || nameLower.includes('amount')) {
            type = 'currency';
          } else if (nameLower.includes('term') || nameLower.includes('scope') || nameLower.includes('description') || nameLower.includes('text') || nameLower.includes('clause')) {
            type = 'textarea';
          } else if (nameLower.includes('number') || nameLower.includes('days') || nameLower.includes('period') || nameLower.includes('count')) {
            type = 'number';
          }

          missingFields.push({
            id,
            name,
            value: '',
            visibility: 'all',
            type
          });
        }
      });

      if (missingFields.length > 0) {
        return [...prev, ...missingFields];
      }
      return prev;
    });
  }, [terms]);

  // Edit mode: 'fill' (smooth fill-in-the-blank) or 'advanced' (raw template + fields sidebar)
  const [editMode, setEditMode] = useState<'fill' | 'advanced'>('fill');
  const [isTemplateCollapsed, setIsTemplateCollapsed] = useState(false);

  // Auto-fill from buyer lead
  useEffect(() => {
    if (selectedBuyerLeadId && isDraft) {
      const lead = leads.find(l => l.id === selectedBuyerLeadId);
      if (lead) {
        setFields(prev => prev.map(f => {
          if (f.id === 'buyer_name' && !f.value) return { ...f, value: lead.name };
          if (f.id === 'purchase_price' && !f.value && lead.offerAmount) return { ...f, value: `$${lead.offerAmount}` };
          return f;
        }));
      }
    }
  }, [selectedBuyerLeadId, leads, isDraft]);

  // Auto-fill from seller lead
  useEffect(() => {
    if (selectedSellerLeadId && isDraft) {
      const lead = leads.find(l => l.id === selectedSellerLeadId);
      if (lead) {
        setFields(prev => prev.map(f => {
          if (f.id === 'seller_name' && !f.value) return { ...f, value: lead.name };
          if (f.id === 'property_address' && !f.value) return { ...f, value: lead.propertyAddress || '' };
          if (f.id === 'purchase_price' && !f.value && lead.offerAmount) return { ...f, value: `$${lead.offerAmount}` };
          return f;
        }));
      }
    }
  }, [selectedSellerLeadId, leads, isDraft]);

  const updateField = (id: string, updates: Partial<ContractField>) => {
    setFields(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const addField = () => {
    const id = `field_${Math.random().toString(36).substr(2, 9)}`;
    setFields([...fields, { id, name: 'New Field', value: '', visibility: 'all', type: 'text' }]);
  };

  const removeField = (id: string) => {
    setFields(fields.filter(f => f.id !== id));
  };

  const handleSave = () => {
    onSave({ fields, terms }, selectedSellerLeadId || selectedBuyerLeadId, selectedBuyerLeadId, selectedSellerLeadId);
  };

  const parsedTerms = useMemo(() => parseTerms(terms), [terms]);

  // Ref to handle auto-resizing textareas in fill mode
  const handleTextareaResize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  return (
    <div className="flex-1 flex gap-4 min-h-0 h-full">
      {/* --- LEFT COLUMN (38%): FIELDS, CUSTOMIZATION, VISIBILITY --- */}
      <div className="w-[38%] flex flex-col gap-3 h-full overflow-hidden pr-2 shrink-0">
        {/* Lead Selectors */}
        <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-3 flex flex-col gap-2.5">
          <span className="text-xs font-bold uppercase tracking-wider text-[var(--t-text-muted)] flex items-center gap-1.5">
            <User size={13} className="text-[var(--t-primary)]" />
            Associated Leads
          </span>
          
          <div className="flex flex-col gap-2">
            <div>
              <label className="block text-[10px] font-semibold text-[var(--t-text-muted)] mb-1 uppercase tracking-wide">Buyer Lead</label>
              <select
                value={selectedBuyerLeadId || ''}
                onChange={(e) => setSelectedBuyerLeadId(e.target.value || null)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-3 py-1.5 text-xs text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)]"
                disabled={!isDraft}
              >
                <option value="">No associated buyer lead</option>
                {leads.filter(l => l.lead_type === 'buyer' || l.lead_type === 'both').map(lead => (
                  <option key={lead.id} value={lead.id}>
                    {lead.name} {lead.budget_max ? `($${lead.budget_max})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-[var(--t-text-muted)] mb-1 uppercase tracking-wide">Seller Lead</label>
              <select
                value={selectedSellerLeadId || ''}
                onChange={(e) => setSelectedSellerLeadId(e.target.value || null)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-3 py-1.5 text-xs text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)]"
                disabled={!isDraft}
              >
                <option value="">No associated seller lead</option>
                {leads.filter(l => l.lead_type === 'seller' || l.lead_type === 'both' || !l.lead_type).map(lead => (
                  <option key={lead.id} value={lead.id}>
                    {lead.name} {lead.propertyAddress ? `— ${lead.propertyAddress.split(',')[0]}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {(selectedBuyerLeadId || selectedSellerLeadId) && isDraft && (
            <span className="text-[9px] text-[var(--t-text-muted)] italic">Auto-filling known fields...</span>
          )}
        </div>

        {/* Mode Toggle */}
        <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-2 flex items-center justify-between shrink-0">
          <span className="text-xs font-bold uppercase tracking-wider text-[var(--t-text-muted)]">Editor Mode</span>
          <div className="flex items-center bg-[var(--t-surface-hover)] rounded-xl p-1 border border-[var(--t-border)]">
            <button
              type="button"
              onClick={() => setEditMode('fill')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${editMode === 'fill' ? 'bg-[var(--t-primary)] text-white shadow' : 'text-[var(--t-text-muted)] hover:text-[var(--t-text)]'}`}
            >
              <PenTool size={13} /> Fill Blanks
            </button>
            <button
              type="button"
              onClick={() => setEditMode('advanced')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${editMode === 'advanced' ? 'bg-[var(--t-primary)] text-white shadow' : 'text-[var(--t-text-muted)] hover:text-[var(--t-text)]'}`}
            >
              <Code size={13} /> Edit Template
            </button>
          </div>
        </div>

        {/* Dynamic Left Column Panels */}
        {editMode === 'fill' ? (
          <div className="flex-1 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-3 flex flex-col gap-2.5 overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[var(--t-text)] text-sm">Contract Fields</h3>
            </div>
            <div className="space-y-5">
              {fields.map(field => (
                <div key={field.id} className="flex flex-col gap-2 bg-[var(--t-bg)] border border-[var(--t-border-subtle)] p-3.5 rounded-2xl">
                  <div className="flex items-center justify-between pb-1 border-b border-[var(--t-border-subtle)]">
                    <label className="text-xs font-bold text-[var(--t-text)]">
                      {field.name}
                    </label>
                    <VisibilityBadge
                      visibility={field.visibility}
                      onChange={(v) => updateField(field.id, { visibility: v })}
                    />
                  </div>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={field.value}
                      onChange={(e) => updateField(field.id, { value: e.target.value })}
                      placeholder={`Enter ${field.name.toLowerCase()}...`}
                      className="w-full px-3 py-1.5 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] placeholder:text-[var(--t-text-muted)] min-h-[60px] resize-none"
                    />
                  ) : (
                    <input
                      type={field.type === 'date' ? 'date' : 'text'}
                      value={field.value}
                      onChange={(e) => updateField(field.id, { value: e.target.value })}
                      placeholder={`Enter ${field.name.toLowerCase()}...`}
                      className="w-full px-3 py-1.5 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] placeholder:text-[var(--t-text-muted)] min-h-[36px]"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col gap-4 min-h-0">
            {/* Fields Customization */}
            <div className="flex-1 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-4 flex flex-col gap-3 overflow-y-auto custom-scrollbar">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-[var(--t-text)] text-sm">Merge Fields</h3>
                <button type="button" onClick={addField} className="p-1 text-[var(--t-primary)] hover:bg-[var(--t-primary-dim)] rounded transition-colors" title="Add field">
                  <Plus size={15} />
                </button>
              </div>

              <div className="text-[10px] text-[var(--t-text-muted)] flex items-center gap-1 px-2 py-1 bg-[var(--t-surface-hover)] rounded-lg">
                <Lock size={10} className="shrink-0" />
                <span>Configure field properties and values.</span>
              </div>

              <div className="space-y-3">
                {fields.map(field => (
                  <div key={field.id} className="group relative bg-[var(--t-bg)] border border-[var(--t-border-subtle)] p-3 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <input
                        value={field.name}
                        onChange={(e) => updateField(field.id, { name: e.target.value })}
                        className="bg-transparent font-medium text-xs text-[var(--t-text)] focus:outline-none w-2/5 min-w-0"
                        placeholder="Field Name"
                      />
                      <div className="flex items-center gap-1">
                        <VisibilityBadge
                          visibility={field.visibility}
                          onChange={(v) => updateField(field.id, { visibility: v })}
                        />
                        <button
                          type="button"
                          onClick={() => removeField(field.id)}
                          className="p-1 text-[var(--t-text-muted)] hover:text-[var(--t-error)] hover:bg-[var(--t-error)]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>

                    {field.type === 'textarea' ? (
                      <textarea
                        value={field.value}
                        onChange={(e) => updateField(field.id, { value: e.target.value })}
                        className="w-full bg-transparent text-xs text-[var(--t-text)] focus:outline-none resize-none min-h-[40px]"
                        placeholder="Enter value..."
                      />
                    ) : (
                      <input
                        type={field.type === 'date' ? 'date' : 'text'}
                        value={field.value}
                        onChange={(e) => updateField(field.id, { value: e.target.value })}
                        className="w-full bg-transparent text-xs text-[var(--t-text)] focus:outline-none"
                        placeholder="Enter value..."
                      />
                    )}

                    <div className="mt-1 text-[9px] text-[var(--t-text-muted)] font-mono bg-[var(--t-surface-hover)] px-2 py-0.5 rounded inline-block select-all cursor-pointer">
                      {`{{${field.id}}}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Template Terms Editor (collapsible) */}
            <div className={`bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl flex flex-col shrink-0 overflow-hidden transition-all duration-200 ${isTemplateCollapsed ? '' : 'h-[220px]'}`}>
              <button
                type="button"
                onClick={() => setIsTemplateCollapsed(c => !c)}
                className="flex items-center justify-between px-4 py-3 shrink-0 hover:bg-[var(--t-surface-hover)] transition-colors"
              >
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-[var(--t-text)] text-sm">Template Content</h3>
                  <span className="text-[10px] text-[var(--t-text-muted)]">Use {"{{field_id}}"}</span>
                </div>
                {isTemplateCollapsed ? <ChevronDown size={14} className="text-[var(--t-text-muted)]" /> : <ChevronUp size={14} className="text-[var(--t-text-muted)]" />}
              </button>
              {!isTemplateCollapsed && (
                <div className="flex-1 px-4 pb-4">
                  <textarea
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                    className="w-full h-full bg-[var(--t-bg)] border border-[var(--t-border-subtle)] rounded-xl p-3 text-xs text-[var(--t-text)] font-mono focus:outline-none focus:border-[var(--t-primary)] resize-none custom-scrollbar leading-relaxed"
                    placeholder="Enter contract terms here..."
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-2 border-t border-[var(--t-border)] shrink-0 flex gap-2">
          <button
            onClick={handleSave}
            className="w-full py-2.5 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)]"
          >
            {isDraft ? 'Save Draft' : 'Save as New Version (Counter)'}
          </button>
        </div>
      </div>

      {/* --- RIGHT COLUMN (62%): LIVE PREVIEW (LARGE, READABLE, REAL-TIME UPDATE) --- */}
      <div className="flex-1 h-full bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl flex flex-col overflow-hidden min-w-0">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-[var(--t-border)] shrink-0">
          <h3 className="font-bold text-[var(--t-text)] text-sm flex items-center gap-2">
            <Eye size={15} className="text-[var(--t-primary)]" />
            Live Preview
          </h3>
          <span className="text-[10px] bg-[var(--t-primary-dim)] text-[var(--t-primary)] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider">
            Real-time update
          </span>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-4 pb-4">
          <div ref={previewContainerRef} className="bg-white text-gray-900 shadow-lg border border-gray-200 rounded-xl p-8 sm:p-12 font-serif text-sm md:text-base leading-relaxed whitespace-pre-wrap min-h-full">
            {parsedTerms.map((part, i) => {
              if (part.type === 'text') {
                return <span key={i}>{part.content}</span>;
              }

              const field = fields.find(f => f.id === part.id);
              if (!field) {
                return (
                  <span 
                    key={i} 
                    className="inline-block bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded text-xs font-sans font-medium"
                    title={`Token: {{${part.id}}}`}
                  >
                    [{part.id} missing]
                  </span>
                );
              }

              if (field.type === 'textarea') {
                return (
                  <div key={i} className="block my-4">
                    <textarea
                      value={field.value}
                      onChange={(e) => {
                        updateField(field.id, { value: e.target.value });
                        handleTextareaResize(e);
                      }}
                      onFocus={(e) => handleTextareaResize(e)}
                      onKeyDown={handleKeyDown}
                      placeholder={`[Enter ${field.name}...]`}
                      className="w-full bg-[var(--t-primary-dim)]/5 hover:bg-[var(--t-primary-dim)]/10 border-2 border-dashed border-[var(--t-primary)]/40 focus:border-solid focus:border-[var(--t-primary)] focus:bg-[var(--t-primary-dim)]/20 rounded-xl p-3 text-sm font-sans focus:outline-none transition-all resize-none overflow-hidden min-h-[80px]"
                    />
                  </div>
                );
              }

              // Inline input calculation
              const minWidth = Math.max(field.name.length, 10) + 2;
              const currentWidth = Math.max(field.value.length, minWidth);

              return (
                <span key={i} className="inline-block relative group px-1">
                  <input
                    type={field.type === 'date' ? 'date' : 'text'}
                    value={field.value}
                    onChange={(e) => updateField(field.id, { value: e.target.value })}
                    onKeyDown={handleKeyDown}
                    placeholder={`[${field.name}]`}
                    className="inline-block bg-[var(--t-primary-dim)]/5 hover:bg-[var(--t-primary-dim)]/10 border-b-2 border-dashed border-[var(--t-primary)]/50 text-[var(--t-primary)] font-bold focus:outline-none focus:border-solid focus:border-[var(--t-primary)] focus:bg-[var(--t-primary-dim)]/20 rounded-t px-2 py-0.5 transition-all text-center placeholder:text-[var(--t-primary)]/40 placeholder:font-semibold"
                    style={{ width: `${currentWidth}ch` }}
                  />
                  {/* Tooltip for field info */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-[var(--t-surface)] border border-[var(--t-border)] shadow-lg rounded text-[10px] whitespace-nowrap opacity-0 group-focus-within:opacity-100 pointer-events-none transition-opacity z-10 flex items-center gap-1 font-sans font-normal text-[var(--t-text)]">
                    {field.name}
                    {field.visibility !== 'all' && (
                      <span className={`px-1 rounded bg-[var(--t-bg)] border border-[var(--t-border-subtle)] ${field.visibility === 'agent_only' ? 'text-[var(--t-error)]' : 'text-[var(--t-info)]'}`}>
                        {field.visibility.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
